import EmailIcon from "@assets/icons/new/email_32px.svg?react";
import Team from "@assets/icons/new/team.svg?react";
import UserIcon from "@assets/icons/new/user_32px.svg?react";
import { AutocompleteOptionType } from "@components/common/members-autocomplete/members-autocomplete-types";
import {
  ICON_SMALL_STYLE,
  SphereAvatar,
} from "@components/header/sphere-avatar";
import { isGroupMember } from "@custom-types/member-type-guards";
import { ArrowDownIcon, neutral, NO_TRANSLATE_CLASS } from "@flat-ui";
import { Box } from "@mui/material";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  fetchingTeamsFlagsSelector,
  getTeamByIdSelector,
} from "@store/teams/teams-selector";
import { fetchTeamDetails } from "@store/teams/teams-slice-thunk";
import { EDecimalToHex, colorConst } from "@styles/common-colors";
import {
  DEFAULT_FONT_FAMILY_ITALIC,
  withEllipsis,
} from "@styles/common-styles";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { isTeam } from "@utils/team-utils";
import { addTransparency } from "@utils/ui-utils";
import {
  getPrettyName,
  getUserInitials,
  isMemberActive,
} from "@utils/user-utils";
import { useCallback, useEffect, useMemo } from "react";

/** Defines the type of the member to be selected */
export type SelectedMember =
  /** String means that a new member will be invited by email */
  | string

  /** A group member means that an existing member was selected */
  | FaroCoreApiTypes.GroupMember

  /** Null means that no member was selected */
  | null;

/**
 * Used to map the details of the current selection to something
 * that this component can show inside the box.
 */
interface SelectionDetails {
  /** The first row of the element, normally containing the member's name */
  title: string;

  /** Whether the title should be translated. Default false since we normally show user information there */
  shouldTranslateTitle?: boolean;

  /** The second row of the element, normally containing the member's email. */
  subtitle?: string;

  /** Whether the subtitle should be translated. Default false since we normally show user information there */
  shouldTranslateSubtitle?: boolean;

  /**
   * Flag whether the component is showing just the placeholder.
   * This is used to change the style of the component when no member have been selected.
   */
  isPlaceholder: boolean;

  /** The initials of the selected member. */
  initials?: string;

  /** The avatar of the selected member, */
  avatarSrc?: string;

  /** Optional icon to be shown, for example an email icon if a new member will be invited. */
  avatarIcon?: JSX.Element;

  /** The type that will represent the member */
  type?: AutocompleteOptionType | string;
}

interface Props {
  /** Stores the member that the user selected from the autocomplete or the email that the user typed. */
  selectedMember?: SelectedMember;

  /** Text to be shows as placeholder in the selection box when no member has been selected. */
  placeholder: string;

  /** Triggered when the user clicks on this component and it is not disabled. */
  onClick: () => void;

  /** Optional flag whether clicking should be disabled */
  isDisabled?: boolean;

  /**
   * Optional flag whether the whole component should be hidden.
   * Useful to alternate in the parent between showing a text field and this component.
   */
  isHidden?: boolean;
}

/** The width of the wrapper for the avatar in pixels. */
const AVATAR_WIDTH = 50;

/** The width of the wrapper for the arrow in pixels. */
const DOWN_ARROW_WIDTH = 30;

/**
 * This component is intended to be used inside member-autocomplete
 * And shows the selection for a single user from an autocomplete dropdown.
 */
export function MemberAutocompleteSelection({
  selectedMember,
  placeholder,
  onClick,
  isDisabled = true,
  isHidden = false,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { isFetchingTeamDetails } = useAppSelector(fetchingTeamsFlagsSelector);
  const selectedTeam = useAppSelector(
    getTeamByIdSelector(
      isGroupMember(selectedMember) ? selectedMember.identity : undefined
    )
  );

  /** If a team was selected, fetch its details to show the number of members. */
  useEffect(() => {
    if (
      selectedMember &&
      isGroupMember(selectedMember) &&
      isTeam(selectedMember) &&
      !selectedTeam
    ) {
      void dispatch(fetchTeamDetails({ teamId: selectedMember.identity }));
    }
  }, [selectedMember, dispatch, selectedTeam]);

  /**
   * Gets the subtitle to show for a selected team
   * E.g. "Team - 15 members" or "Team - Loading ..."
   */
  const getTeamSubtitle = useCallback(() => {
    if (isFetchingTeamDetails === "pending") {
      return `${capitalizeFirstLetter(TEAM_DISPLAY_NAME)} - Loading...`;
    }
    return `${capitalizeFirstLetter(TEAM_DISPLAY_NAME)} - ${
      selectedTeam?.memberCount
    } members`;
  }, [selectedTeam?.memberCount, isFetchingTeamDetails]);

  /**
   * Defines the text and other elements that will be shown in the text.
   * This is useful so that the JSX part of the component is as generic as possible.
   */
  const selectionDetails: SelectionDetails = useMemo(() => {
    /** If an object was selected it means that an existing member was selected. */
    if (isGroupMember(selectedMember)) {
      if (isTeam(selectedMember)) {
        /** If the selected member is a team, show the team name and member count. */
        return {
          title: getPrettyName(selectedMember),
          subtitle: getTeamSubtitle(),
          // The subtitle is translated because we show text like "Team - X members"
          shouldTranslateSubtitle: true,
          type: "team",
          isPlaceholder: false,
        };
      } else {
        return {
          title: getPrettyName(selectedMember),
          // The subtitle is not translated because it shows the user's email
          subtitle: isMemberActive(selectedMember)
            ? selectedMember.email
            : undefined,
          isPlaceholder: false,
          initials: getUserInitials(selectedMember),
          type: "member",
          avatarSrc: selectedMember.thumbnailUrl,
          avatarIcon: undefined,
        };
      }
    }

    /** If a string was selected it means that a new member will be invited by email. */
    if (selectedMember && typeof selectedMember === "string") {
      return {
        title: selectedMember,
        subtitle: undefined,
        isPlaceholder: false,
        initials: undefined,
        avatarSrc: undefined,
        avatarIcon: <EmailIcon style={ICON_SMALL_STYLE} />,
      };
    }

    /** Otherwise no member was selected. Show the placeholder in this case. */
    return {
      title: placeholder,
      shouldTranslateTitle: true,
      subtitle: undefined,
      isPlaceholder: true,
      initials: undefined,
      avatarSrc: undefined,
      avatarIcon: <UserIcon style={ICON_SMALL_STYLE} />,
    };
  }, [selectedMember, placeholder, getTeamSubtitle]);

  /** Defines the font color of the text based on whether it is disabled and/or a placeholder is shown. */
  const fontColor = useMemo(() => {
    if (isDisabled) {
      return addTransparency({
        color: neutral[500],
        alpha: EDecimalToHex.hundredTwo,
      });
    }
    return selectionDetails.isPlaceholder
      ? colorConst.placeholder
      : colorConst.normalFont;
  }, [isDisabled, selectionDetails]);

  return (
    <Box
      component="div"
      onClick={() => !isDisabled && onClick()}
      sx={{
        height: "60px",
        display: isHidden ? "none" : "flex",
        alignItems: "center",
        width: "100%",
        backgroundColor: isDisabled ? neutral[50] : neutral[0],
        border: `2px solid ${neutral[100]}`,
        borderRadius: "5px",
        padding: "10px",
        paddingRight: "0px",
        "&:hover": {
          cursor: isDisabled ? "default" : "pointer",
        },
      }}
    >
      <Box
        component="div"
        sx={{
          width: `${AVATAR_WIDTH}px`,
        }}
      >
        {selectionDetails.type === "team" ? (
          <SphereAvatar
            icon={<Team />}
            size="small"
            backgroundColor={neutral[700]}
          />
        ) : (
          <SphereAvatar
            src={selectionDetails.avatarSrc}
            initials={selectionDetails.initials ?? ""}
            icon={selectionDetails.avatarIcon}
          />
        )}
      </Box>
      <Box
        component="div"
        sx={{
          width: `calc(100% - ${AVATAR_WIDTH + DOWN_ARROW_WIDTH}px)`,
          fontFamily: selectionDetails.isPlaceholder
            ? DEFAULT_FONT_FAMILY_ITALIC
            : undefined,
          fontSize: "14px",
          color: fontColor,
        }}
      >
        <Box
          component="div"
          sx={{ ...withEllipsis }}
          className={
            selectionDetails.shouldTranslateTitle ? "" : NO_TRANSLATE_CLASS
          }
        >
          {selectionDetails.title}
        </Box>

        {selectionDetails.subtitle && (
          <Box
            component="div"
            sx={{ ...withEllipsis }}
            className={
              selectionDetails.shouldTranslateSubtitle ? "" : NO_TRANSLATE_CLASS
            }
          >
            {selectionDetails.subtitle}
          </Box>
        )}
      </Box>
      <Box
        component="div"
        sx={{
          width: `${DOWN_ARROW_WIDTH}px`,
          display: "flex",
          alignItems: "center",
          paddingLeft: "9px",
        }}
      >
        <ArrowDownIcon style={{ width: "16px", height: "16px" }} />
      </Box>
    </Box>
  );
}
